import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    CircularProgress,
    Button,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material';
import { tokens } from '../../../../theme';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Axios from 'axios';
import { getAuthHeader } from '../../../../utils/authToken';
import FeedbackHeader from './components/FeedbackHeader';
import EssayColumn from './components/EssayColumn';
import CommentColumn from './components/CommentColumn';
import OverallEvaluation from './components/OverallEvaluation';
import { findHighlights } from './utils/highlightUtils';
import './EssayFeedbackPage.css';

const EssayFeedbackPage = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { id: courseId, hid: homeworkId, answerId } = useParams();
    const navigate = useNavigate();

    // Responsive breakpoints - 3-column layout only on large screens (1200px+)
    const isDesktop = useMediaQuery('(min-width:1200px)');
    const isMobile = !isDesktop;

    // State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hwAnswer, setHwAnswer] = useState(null);
    const [feedbackJson, setFeedbackJson] = useState(null);
    const [studentAnswer, setStudentAnswer] = useState('');
    const [highlights, setHighlights] = useState([]);
    const [activeComment, setActiveComment] = useState(null);
    const [hoveredComment, setHoveredComment] = useState(null);

    // Refs
    const essayRef = useRef(null);

    // Fetch hw_answer data
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await Axios.get(
                    `${process.env.REACT_APP_URL}/api/v1/hw-answers/${answerId}`,
                    {
                        headers: {
                            Authorization: getAuthHeader(),
                        },
                    }
                );

                const data = response.data;
                setHwAnswer(data);
                setFeedbackJson(data.feedback_json);

                // Extract student answer (stored as array, take first element)
                const answerText = Array.isArray(data.answer) ? data.answer[0] : data.answer;
                // Process HTML to preserve paragraph breaks
                const plainText = answerText
                    ? answerText
                        .replace(/<br\s*\/?>/gi, '\n')           // Convert <br> to newlines
                        .replace(/<\/p>/gi, '\n\n')               // Convert </p> to double newlines
                        .replace(/<[^>]*>/g, '')                  // Strip remaining HTML tags
                        .replace(/[ \t]+/g, ' ')                  // Collapse horizontal whitespace only (NOT \n)
                        .replace(/\n{3,}/g, '\n\n')               // Collapse excessive newlines to max 2
                        .trim()
                    : '';
                setStudentAnswer(plainText);

            } catch (err) {
                console.error('Error fetching essay feedback:', err);
                setError(err.response?.data?.error || 'Failed to load feedback');
            } finally {
                setLoading(false);
            }
        };

        if (answerId) {
            fetchData();
        }
    }, [answerId]);

    // Process highlights when feedback is loaded
    useEffect(() => {
        if (feedbackJson && studentAnswer) {
            const processedHighlights = findHighlights(
                studentAnswer,
                feedbackJson.strengths || [],
                feedbackJson.improvements || []
            );
            setHighlights(processedHighlights);
        }
    }, [feedbackJson, studentAnswer]);

    // Handle highlight hover
    const handleHighlightHover = (highlightId) => {
        setHoveredComment(highlightId);
    };

    // Handle highlight leave
    const handleHighlightLeave = () => {
        setHoveredComment(null);
    };

    // Handle highlight click - scroll comment into view
    const handleHighlightClick = (highlightId) => {
        setActiveComment(activeComment === highlightId ? null : highlightId);
        // Scroll comment into view
        const commentEl = document.getElementById(`comment-${highlightId}`);
        if (commentEl) {
            commentEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    // Handle comment click - scroll highlight into view
    const handleCommentClick = (highlightId) => {
        setActiveComment(activeComment === highlightId ? null : highlightId);
        // Scroll highlight into view
        const highlightEl = document.getElementById(`highlight-${highlightId}`);
        if (highlightEl) {
            highlightEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    // Handle comment hover - highlight the corresponding sentence
    const handleCommentHover = (highlightId) => {
        setHoveredComment(highlightId);
    };

    // Handle comment leave
    const handleCommentLeave = () => {
        setHoveredComment(null);
    };

    // Go back handler
    const handleGoBack = () => {
        navigate(-1);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" gap={2}>
                <Typography variant="h5" color="error">{error}</Typography>
                <Button variant="contained" onClick={handleGoBack}>Go Back</Button>
            </Box>
        );
    }

    if (!feedbackJson) {
        return (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" gap={2}>
                <Typography variant="h5">No feedback available for this essay</Typography>
                <Button variant="contained" onClick={handleGoBack}>Go Back</Button>
            </Box>
        );
    }

    // Separate strengths and improvements
    const strengths = highlights.filter(h => h.type === 'strength');
    const improvements = highlights.filter(h => h.type === 'improvement');

    return (
        <Box
            sx={{
                p: isMobile ? 2 : 3,
                maxWidth: '1800px',
                margin: '0 auto',
            }}
        >
            {/* Back Button */}
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleGoBack}
                sx={{ mb: 2 }}
            >
                Back to Homework
            </Button>

            {/* Header */}
            <FeedbackHeader
                score={feedbackJson.score}
                grade={feedbackJson.grade}
                wordCount={feedbackJson.word_count}
                promptAlignment={feedbackJson.prompt_alignment}
                colors={colors}
            />

            {/* Main Content - Three Column Layout (Desktop) or Stacked (Mobile) */}
            {isMobile ? (
                // Mobile: Stacked layout
                <Box sx={{ mt: 3 }}>
                    <EssayColumn
                        ref={essayRef}
                        text={studentAnswer}
                        highlights={highlights}
                        activeComment={activeComment}
                        hoveredComment={hoveredComment}
                        onHighlightClick={handleHighlightClick}
                        onHighlightHover={handleHighlightHover}
                        onHighlightLeave={handleHighlightLeave}
                        colors={colors}
                        isMobile={true}
                        feedbackJson={feedbackJson}
                    />
                </Box>
            ) : (
                // Desktop/Tablet: Three column layout
                <Box
                    sx={{
                        display: 'flex',
                        mt: 3,
                        gap: isDesktop ? 3 : 2,
                        alignItems: 'flex-start',
                    }}
                >
                    {/* Left Column - Improvements */}
                    <Box
                        sx={{
                            flex: isDesktop ? 2.5 : 2,
                        }}
                    >
                        <CommentColumn
                            side="left"
                            comments={improvements}
                            activeComment={activeComment}
                            hoveredComment={hoveredComment}
                            onCommentClick={handleCommentClick}
                            onCommentHover={handleCommentHover}
                            onCommentLeave={handleCommentLeave}
                            colors={colors}
                        />
                    </Box>

                    {/* Center Column - Essay */}
                    <Box
                        sx={{
                            flex: isDesktop ? 5 : 6,
                            minWidth: 0,
                        }}
                    >
                        <EssayColumn
                            ref={essayRef}
                            text={studentAnswer}
                            highlights={highlights}
                            activeComment={activeComment}
                            hoveredComment={hoveredComment}
                            onHighlightClick={handleHighlightClick}
                            onHighlightHover={handleHighlightHover}
                            onHighlightLeave={handleHighlightLeave}
                            colors={colors}
                            isMobile={false}
                        />
                    </Box>

                    {/* Right Column - Strengths */}
                    <Box
                        sx={{
                            flex: isDesktop ? 2.5 : 2,
                        }}
                    >
                        <CommentColumn
                            side="right"
                            comments={strengths}
                            activeComment={activeComment}
                            hoveredComment={hoveredComment}
                            onCommentClick={handleCommentClick}
                            onCommentHover={handleCommentHover}
                            onCommentLeave={handleCommentLeave}
                            colors={colors}
                        />
                    </Box>
                </Box>
            )}

            {/* Overall Evaluation */}
            <OverallEvaluation
                evaluation={feedbackJson.overall_evaluation}
                colors={colors}
            />
        </Box>
    );
};

export default EssayFeedbackPage;
